import { useRef } from "react";
import styles from "./RecommendedOutfits.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import { useRecommendedOutfits } from "../../context/recommendedOutfits/RecommendedOutfitsContext";
import { fireLooksScrollToView } from "../../analytics/looks/looksScrollToView";
import Image from "../image/Image";
import NewPill from "../newPill/NewPill";
import PlusPill from "../plusPill/PlusPill";
import useFiftyPercentInView from "../../hooks/useFiftyPercentInView";
import { RECOMMENDED_OUTFITS_OPEN_MODAL_EVENT } from "./constants.js";
import { handleModalTriggerEvent } from "../../utils/handleModalTriggerEvent";
import { BUTTON_TYPES } from "../buttons/button/ButtonTypes";
import Button from "../buttons/button/Button";

const getRecommendedImages = (recommendations) => {
  return recommendations.map((recommendedProduct) => {
    const fullImageUrl = recommendedProduct.imageUrl.startsWith("http")
      ? recommendedProduct.imageUrl
      : `https://${recommendedProduct.imageUrl}`;

    return (
      <Image
        key={recommendedProduct.id}
        url={fullImageUrl}
        alternateText={recommendedProduct.name}
        type={"hero"}
        width={317}
        loadingType={"lazy"}
      />
    );
  });
};

const numOfOutfitsToShow = (recommendedOutfits, recommendationLength) => {
  if (recommendationLength === 3) {
    return {
      outfitsToShow: recommendedOutfits.slice(0, 2),
      shouldShowPlusPill: true,
    };
  }
  if (recommendationLength > 4) {
    return {
      outfitsToShow: recommendedOutfits.slice(0, 4),
      shouldShowPlusPill: true,
    };
  }
  return { outfitsToShow: recommendedOutfits, shouldShowPlusPill: false };
};

export default function RecommendedOutfits() {
  const { getTranslation, product, isMobile } = useRuntimeConstants();
  const { name, brandName, images } = product;

  const ref = useFiftyPercentInView(() =>
    fireLooksScrollToView({
      isMobile,
    })
  );

  const { recommendedOutfits, hasRecommendationApiLoaded } =
    useRecommendedOutfits();

  const recommendedOutfitsClickRef = useRef();
  const recommendationLength = recommendedOutfits?.length || 0;

  const { outfitsToShow, shouldShowPlusPill } = numOfOutfitsToShow(
    recommendedOutfits,
    recommendationLength
  );

  const { url: primaryUrl } = images.find((img) => img.isPrimary === true);

  const handleClick = () => {
    handleModalTriggerEvent({
      focusOnCloseElement: recommendedOutfitsClickRef.current,
      eventKey: RECOMMENDED_OUTFITS_OPEN_MODAL_EVENT,
    });
  };

  return (
    <div ref={ref}>
      <section
        data-testid="recommendedOutfitsSection"
        className={styles.section}
        aria-label={getTranslation("pdp_styled_for_you")}
      >
        <div className={styles.containerHeader}>
          <Image
            url={primaryUrl}
            alternateText={name}
            type={"thumbnail"}
            width={75}
            loadingType={"lazy"}
            additionalClasses={styles.primaryImage}
          />
          <div className={styles.containerHeaderDetails}>
            <NewPill
              text={getTranslation("generic_onboarding_feature_new_tag")}
            />
            <h2 className={styles.title}>
              {getTranslation("pdp_styled_for_you")}
            </h2>
            <p className={styles.subtitle}>
              {getTranslation("pdp_styled_for_you_subtitle", { brandName })}
            </p>
          </div>
        </div>
        <div
          data-testid="recommendedImageContainer"
          className={styles.skeleton}
        >
          {hasRecommendationApiLoaded && (
            <Button
              handleClick={handleClick}
              aria-label={getTranslation(
                "pdp_styled_for_you_accessibility_multiple_products"
              )}
              type={BUTTON_TYPES.naked}
              buttonRef={recommendedOutfitsClickRef}
              dataTestId={"recommendedOutfitsModalCta"}
              additionalClasses={styles.recommendedContainer}
            >
              {recommendationLength === 1 && (
                <Image
                  url={images[1]?.url}
                  alternateText={name}
                  type={"hero"}
                  width={317}
                  loadingType={"lazy"}
                />
              )}
              {getRecommendedImages(outfitsToShow)}
              {shouldShowPlusPill && (
                <PlusPill
                  number={recommendationLength - outfitsToShow.length}
                ></PlusPill>
              )}
            </Button>
          )}
        </div>
      </section>
    </div>
  );
}
